import { getAssetLoadError } from "../../utils/getAssetLoadError";
import { keepPreviousData } from "@tanstack/react-query";
import type { TrashIndexSort } from "@zapier/bff";
import { RouterOutputs, trpc } from "../trpc";
import {
  FOLDER_ASSETS_PAGE_SIZE,
  FOLDER_ASSETS_SORT_LIMIT,
  DEFAULT_FOLDER_ID,
} from "@zapier/utils";

interface Input {
  enabled?: boolean;
  folderId?: string;
  limit?: number;
  offset?: number;
  ownerId?: number;
  /** Server-side search and sort. Honoured by the trash index path only. */
  search?: string;
  sort?: TrashIndexSort;
}

export type FolderAssets = RouterOutputs["asset"]["getHydratedFolderAssets"];

export function useQueryFolderAssets({
  enabled,
  folderId,
  limit,
  offset = 0,
  ownerId,
  search,
  sort,
}: Input) {
  const query = trpc.asset.getHydratedFolderAssets.useQuery(
    {
      folderId: folderId || DEFAULT_FOLDER_ID,
      limit: limit === 0 ? FOLDER_ASSETS_PAGE_SIZE : limit,
      offset,
      ownerId,
      search,
      sort,
    },
    {
      enabled: folderId !== undefined && enabled,
      // Don't keep previous data when fetching for sorting (larger limit)
      // so we show a loading state
      placeholderData:
        limit === FOLDER_ASSETS_SORT_LIMIT ? undefined : keepPreviousData,
      trpc: {
        context: {
          // Skip batching to avoid bundling this critical procedure with slower requests
          skipBatching: true,
        },
      },
    },
  );

  const { data, isLoading, isFetching, isError, error, refetch } = query;
  const failedAssetTypes = query.isPlaceholderData
    ? []
    : (data?.failedAssetTypes ?? []);
  const loadError =
    getAssetLoadError(query) ??
    (failedAssetTypes.length > 0
      ? {
          hasData: Boolean(data?.results.length),
          hasServiceError: data?.hasServiceError ?? false,
          isRetrying: isFetching,
          retry: refetch,
        }
      : undefined);

  return {
    loadError,
    failedAssetTypes,
    errorStatus: error?.data?.httpStatus,
    isError,
    isFetching: isFetching,
    isLoading: isLoading,
    response: data,
    // If the limit is 0, we should hide the assets.
    // This is useful when we want to
    // determine the total # of assets since we still want a response
    hideAssets: limit === 0,
  };
}
